import { ListView, RadioGroup } from '@modulor/react';
import { nanoid } from '@reduxjs/toolkit';
import { useNavigateWithSearch } from 'Modulor/hooks';
import type { FC } from 'react';
import { useEffect, useLayoutEffect, useState } from 'react';
import IntegrationCommunication from 'services/integration-communication';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import type { PayHubCashierContractsParamOptionResponse } from 'store/models';
import {
  betShopFieldNameSelector,
  betShopOptionsSelector,
  betShopSetValueCallbackSelector,
  currentBetShopSelector,
  setBetShopSelected,
} from 'store/slices/withdrawal';
import { getLayoutType } from 'utils/common';
import { DESKTOP_MODAL_MIN_HEIGHT, LayoutType } from 'utils/constants';

import { useSetHeaderConfig } from '../../components/Header/hooks/useSetHeaderConfig';
import { BetShopsListCell } from './BetShopsListCell';
import type { PreparedCells } from './types';

export const BetShopsWithdrawal: FC = () => {
  const navigate = useNavigateWithSearch();
  const options = useAppSelector(betShopOptionsSelector);
  const setValue = useAppSelector(betShopSetValueCallbackSelector);
  const name = useAppSelector(betShopFieldNameSelector);

  const dispatch = useAppDispatch();
  const currentBetShop = useAppSelector(currentBetShopSelector);

  useSetHeaderConfig({
    preset: 'betshopWithdrawal',
    onNavigationAction: () => navigate(-1),
  });

  const [preparedCells, setpreparedCells] = useState<PreparedCells[]>([]);
  const [expandedCells, setExpandedCells] = useState<string[]>([]);

  const layoutType = getLayoutType();
  const isMobileLayout = layoutType === LayoutType.mobile;

  useEffect(() => {
    const updatedOptions: PreparedCells[] = options?.map((option) => ({
      ...option,
      key: nanoid(),
    }));

    setpreparedCells(updatedOptions);
  }, [options.length]);

  useEffect(() => {
    const betShopForExpand = preparedCells.find((option) => option.label === currentBetShop?.label);
    if (currentBetShop?.value && betShopForExpand) {
      setExpandedCells([...expandedCells, betShopForExpand.key]);
    }
    return () => setExpandedCells([]);
  }, [currentBetShop, options]);

  useLayoutEffect(() => {
    if (!isMobileLayout) {
      const { height = 350 } = window.document.getElementById('root')?.getBoundingClientRect() || {};
      setTimeout(() => {
        IntegrationCommunication.sendMessage({
          id: 'frameChangedHeight',
          value: height <= DESKTOP_MODAL_MIN_HEIGHT ? DESKTOP_MODAL_MIN_HEIGHT + 50 : height,
        });
      }, 0);
    }
  }, [isMobileLayout]);

  const onSelectOption = (option: PayHubCashierContractsParamOptionResponse): void => {
    dispatch(setBetShopSelected(option));
    if (setValue) {
      setValue(name, option.value, {
        shouldValidate: true,
      });
      window.history.back();
    }
  };

  return (
    <ListView className="list-wrapper">
      <RadioGroup onChange={() => {}}>
        {preparedCells?.map((cell) => (
          <BetShopsListCell
            key={cell.key}
            rootCell={cell}
            currentBetShop={currentBetShop}
            expandedCells={expandedCells}
            setExpandedCells={setExpandedCells}
            onSelectOption={onSelectOption}
          />
        ))}
      </RadioGroup>
    </ListView>
  );
};
